'use client'
import type { Product, ProductSize } from 'product/types/Product'
import type { Stock, StockAvailability } from 'product/types/Stock'
import { useCallback, useMemo } from 'react'

interface UseProductColorsProps {
	product: Product
	productId: string
	selectedColorId: string
	defaultWareHouseId?: string
	delayedWareHouse?: (warehousesId?: string[]) => boolean
}

export interface DelayedSize {
	isDelayed: boolean
}

const getAvailabilityInfo = (
	size: ProductSize,
	selectedStockSizes?: Record<string, StockAvailability>,
	delayedWareHouse?: (warehousesId?: string[]) => boolean
): {
	available: boolean
	isDelayed: boolean
	warehouses?: string[]
	lastUnits?: boolean
} => {
	if (!selectedStockSizes?.[size.id]) {
		return {
			available: true,
			isDelayed: false,
		}
	}
	const info = {
		available: selectedStockSizes[size.id].available,
		warehouses: selectedStockSizes[size.id].warehouses,
		lastUnits: selectedStockSizes[size.id].lastUnits,
		isDelayed: false,
	}
	if (delayedWareHouse) {
		info.isDelayed = delayedWareHouse(selectedStockSizes[size.id].warehouses)
	}
	return info
}

export function useProductSelectedColorSizes({
	product,
	productId,
	selectedColorId,
	delayedWareHouse,
}: UseProductColorsProps): {
	getSizes: (stock?: Stock) => (ProductSize & StockAvailability & DelayedSize)[]
	selectedProductId: string
} {
	const productColors = useMemo(() => product?.colors, [product])
	const selectedColor = useMemo(
		() => productColors?.find((item) => item.id === selectedColorId),
		[productColors, selectedColorId]
	)

	const getSizes = useCallback(
		(stock?: Stock) => {
			if (!selectedColor) {
				return []
			}

			const selectedStockSizes = stock?.colors[selectedColorId]?.sizes

			return (
				selectedColor.sizes
					/**
					 * Filter sizes that are available in stock.
					 *
					 * In case of related sizes we have two options:
					 * - Set preComputedStock with the related sizes
					 * - Filter only the sizes that are available in stock
					 */
					.filter((size) => !!selectedStockSizes?.[size.id])
					.map((size) => ({
						...size,
						...getAvailabilityInfo(size, selectedStockSizes, delayedWareHouse),
					}))
			)
		},
		[selectedColor, delayedWareHouse, selectedColorId]
	)

	return useMemo(
		() => ({
			getSizes,
			selectedProductId: selectedColor?.relatedProductId ?? productId,
		}),
		[productId, selectedColor, getSizes]
	)
}
